import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Database, CalendarDays,
  CheckCircle, RefreshCw, Clock, Info, Layers,
} from 'lucide-react';
import api from '../services/api';

// Collections that are reference data (always full, clearly labelled)
const REFERENCE_COLLECTIONS = new Set([
  'catalog', 'stages', 'gold_prices', 'diamond_prices', 'diamond_series',
  'diamond_shape_types', 'diamond_size_categories', 'diamond_sieve_sizes',
  'lead_reasons', 'working_hours',
]);

function formatDate(isoStr) {
  if (!isoStr) return null;
  return new Date(isoStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function DataExport() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [lastExportAt, setLastExportAt] = useState(null); // ISO string from server
  const [summary, setSummary]         = useState(null);
  const [exportMode, setExportMode]   = useState(null);   // 'full'|'incremental'|'date_range'
  const [totalRecords, setTotalRecords] = useState(null);
  const [exportedAt, setExportedAt]   = useState(null);
  const [collCount, setCollCount]     = useState(null);
  const [error, setError]             = useState('');

  // Load last export timestamp on mount
  useEffect(() => {
    api.get('/export/meta')
      .then(r => setLastExportAt(r.data?.last_export_at || null))
      .catch(() => {})
      .finally(() => setLoadingMeta(false));
  }, []);

  const triggerDownload = async (params) => {
    setLoading(true);
    setError('');
    setSummary(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await api.get(`/export/data?${qs}`, { responseType: 'blob' });

      // Derive filename from header
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `diaforge_export_${today}.json`;

      // Trigger file download
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Parse summary from blob for success card
      try {
        const text = await new Blob([res.data]).text();
        const parsed = JSON.parse(text);
        setSummary(parsed.summary || {});
        setExportMode(parsed.export_mode);
        setTotalRecords(parsed.total_records);
        setExportedAt(parsed.exported_at);
        setCollCount(parsed.collections_exported);
        setLastExportAt(parsed.exported_at); // update local state
      } catch { /* summary display is best-effort */ }

    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (msg) {
        setError(msg);
      } else {
        // Try to read blob error
        try {
          const text = await err?.response?.data?.text();
          const parsed = JSON.parse(text);
          setError(parsed?.detail || 'Export failed. Please try again.');
        } catch {
          setError('Export failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFullExport = () => triggerDownload({});
  const handleRangeExport = () => {
    const p = {};
    if (fromDate) p.from_date = fromDate;
    if (toDate)   p.to_date   = toDate;
    triggerDownload(p);
  };
  const handleIncrementalExport = () => {
    if (!lastExportAt) return;
    triggerDownload({ since: lastExportAt });
  };

  const hasDateFilter = fromDate || toDate;
  const hasLastExport = !!lastExportAt;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-[#075E54] text-white shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-1 rounded-full hover:bg-white/10 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-base font-semibold leading-tight">Data Export</h1>
          <p className="text-white/60 text-xs">Download your business data</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">

        {/* Last export info */}
        {!loadingMeta && (
          <div className={`rounded-2xl px-4 py-3 flex items-start gap-3 ${hasLastExport ? 'bg-emerald-50 border border-emerald-100' : 'bg-blue-50 border border-blue-100'}`}>
            <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hasLastExport ? 'text-emerald-600' : 'text-blue-500'}`} />
            <div>
              {hasLastExport ? (
                <>
                  <p className="text-xs font-semibold text-emerald-700">Last export</p>
                  <p className="text-xs text-emerald-600">{formatDate(lastExportAt)}</p>
                </>
              ) : (
                <p className="text-xs text-blue-600">No previous export found. Use <strong>Full Export</strong> for your first download.</p>
              )}
            </div>
          </div>
        )}

        {/* Auto-discover note */}
        <div className="bg-white rounded-2xl shadow-sm px-4 py-3 flex items-start gap-3">
          <Layers className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-gray-700">Auto-discovers all modules</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Every collection in the database is automatically included — including any new modules added in the future. No manual updates needed.
            </p>
          </div>
        </div>

        {/* Export Modes */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 pt-4 pb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Export Modes</p>
          </div>

          {/* Mode 1: Incremental — primary, shown if last export exists */}
          {hasLastExport && (
            <div className="mx-4 mb-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="flex items-start gap-2 mb-2">
                <RefreshCw className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">Since Last Download</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Only new &amp; updated records since <strong>{formatDate(lastExportAt)}</strong>
                  </p>
                  <p className="text-xs text-emerald-500 mt-0.5">
                    Transactional data only — reference data (catalog, prices) always included in full.
                  </p>
                </div>
              </div>
              <button
                onClick={handleIncrementalExport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
                data-testid="export-incremental-btn"
              >
                <RefreshCw className="w-4 h-4" />
                {loading ? 'Exporting…' : 'Export New Records Only'}
              </button>
            </div>
          )}

          {/* Mode 2: Date Range */}
          <div className="mx-4 mb-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-700">Date Range <span className="text-gray-400 font-normal text-xs">(manual)</span></p>
            </div>
            <p className="text-xs text-gray-400 mb-2">Filter orders, leads &amp; estimates by date.</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From</label>
                <input
                  type="date" max={today} value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="from-date-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">To</label>
                <input
                  type="date" max={today} value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="to-date-input"
                />
              </div>
            </div>
            {hasDateFilter && (
              <button
                onClick={handleRangeExport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#075E54] text-white font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
                data-testid="export-range-btn"
              >
                <CalendarDays className="w-4 h-4" />
                {loading ? 'Exporting…' : `Export ${fromDate || '…'}${fromDate && toDate ? ' → ' : ''}${toDate || ''}`}
              </button>
            )}
            {!hasDateFilter && (
              <p className="text-center text-xs text-gray-400 mt-1">Set dates above to enable</p>
            )}
          </div>

          {/* Mode 3: Full Export */}
          <div className="mx-4 mb-4 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Full Export</p>
                <p className="text-xs text-gray-400">Complete snapshot of everything</p>
              </div>
            </div>
            <button
              onClick={handleFullExport}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-[#075E54] text-[#075E54] font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
              data-testid="export-all-btn"
            >
              <Download className="w-4 h-4" />
              {loading ? 'Exporting…' : 'Export Everything'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600" data-testid="export-error">
            {error}
          </div>
        )}

        {/* Success Summary */}
        {summary && exportedAt && (
          <div className="bg-white rounded-2xl shadow-sm px-4 py-4" data-testid="export-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <p className="text-sm font-semibold text-gray-800">
                Export successful
                {exportMode === 'incremental' && <span className="ml-2 text-xs text-emerald-600 font-normal bg-emerald-50 px-2 py-0.5 rounded-full">Incremental</span>}
                {exportMode === 'full' && <span className="ml-2 text-xs text-gray-500 font-normal bg-gray-100 px-2 py-0.5 rounded-full">Full</span>}
                {exportMode === 'date_range' && <span className="ml-2 text-xs text-blue-600 font-normal bg-blue-50 px-2 py-0.5 rounded-full">Date Range</span>}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-gray-50 rounded-xl px-3 py-2 text-center">
                <p className="text-lg font-bold text-gray-800">{(totalRecords || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400">Total Records</p>
              </div>
              <div className="bg-gray-50 rounded-xl px-3 py-2 text-center">
                <p className="text-lg font-bold text-gray-800">{collCount || 0}</p>
                <p className="text-xs text-gray-400">Collections</p>
              </div>
              <div className="bg-gray-50 rounded-xl px-3 py-2 text-center">
                <p className="text-xs font-bold text-gray-800 leading-tight">{formatDate(exportedAt)}</p>
                <p className="text-xs text-gray-400 mt-0.5">Exported At</p>
              </div>
            </div>
            {/* Per-collection breakdown */}
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {Object.entries(summary)
                .filter(([, v]) => v > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([col, count]) => (
                  <div key={col} className="flex items-center justify-between py-0.5">
                    <span className="text-xs text-gray-500 capitalize">{col.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-2">
                      {REFERENCE_COLLECTIONS.has(col) && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 rounded">ref</span>
                      )}
                      <span className="text-xs font-semibold text-gray-700">{count.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Format note */}
        <div className="flex items-start gap-2 px-1 pb-4">
          <Info className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-400">
            Downloaded as <strong>.json</strong> — open in any text editor, import into Excel or Google Sheets. Filename includes export type &amp; timestamp. Only visible to Admin &amp; Owner roles.
          </p>
        </div>

      </div>
    </div>
  );
}
